<script setup lang="ts">
import { computed, ref } from "vue";
import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Diamond,
  EyeOff,
  GripVertical,
  Link2,
  LockKeyhole,
  ZoomIn,
} from "lucide-vue-next";
import ScheduleImpactDialog from "@/components/views/ScheduleImpactDialog.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import {
  addDays,
  addWorkingDays,
  daysBetween,
  shortDate,
  todayLocalDate,
} from "@/utils/dates";
import type {
  ProjectSection,
  ProjectTask,
  ScheduleImpact,
} from "@/types/models";

const props = defineProps<{
  tasks: ProjectTask[];
  sections: ProjectSection[];
}>();
const emit = defineEmits<{ openTask: [id: string] }>();
const workspace = useWorkspaceStore();
const scale = ref<"day" | "week">("week");
const expanded = ref(false);
const timelineScroller = ref<HTMLElement | null>(null);
const rows = computed(() =>
  props.tasks.filter((task) => task.type !== "summary" || task.level === 0),
);
const timelineStart = computed(() => {
  let start = workspace.active?.project.startDate || todayLocalDate();
  rows.value.forEach((task) => {
    if (task.startDate && task.startDate < start) start = task.startDate;
  });
  return start;
});
const timelineEnd = computed(() => {
  let end =
    workspace.active?.project.targetEndDate ||
    workspace.active?.project.startDate ||
    todayLocalDate();
  rows.value.forEach((task) => {
    if (task.dueDate && task.dueDate > end) end = task.dueDate;
  });
  return end;
});
const timelineDays = computed(() => {
  const step = scale.value === "day" ? 1 : 7;
  const minimumDays = scale.value === "day" ? 42 : 105;
  const projectDays = daysBetween(timelineStart.value, timelineEnd.value) + 1;
  return Math.ceil(Math.max(minimumDays, projectDays) / step) * step;
});
const dayWidth = computed(() =>
  scale.value === "day" ? (expanded.value ? 40 : 30) : expanded.value ? 22 : 16,
);
const dragging = ref<{
  task: ProjectTask;
  startX: number;
  shift: number;
} | null>(null);
const impact = ref<ScheduleImpact | null>(null);
const timeMarkers = computed(() => {
  const step = scale.value === "day" ? 1 : 7;
  return Array.from(
    { length: Math.ceil(timelineDays.value / step) },
    (_, index) => ({
      date: addDays(timelineStart.value, index * step),
      left: index * step * dayWidth.value,
      width: step * dayWidth.value,
    }),
  );
});
const timelineWidth = computed(() => timelineDays.value * dayWidth.value);
const gridSize = computed(() =>
  scale.value === "day" ? dayWidth.value : dayWidth.value * 7,
);
const todayLeft = computed(
  () => daysBetween(timelineStart.value, todayLocalDate()) * dayWidth.value,
);
const boundaryLeft = computed(
  () =>
    daysBetween(
      timelineStart.value,
      workspace.active?.project.targetEndDate || todayLocalDate(),
    ) * dayWidth.value,
);
const impactIds = computed(() => {
  const ids = new Set<string>();
  if (!impact.value) return ids;
  ids.add(impact.value.taskId);
  impact.value.affected.forEach((item) => ids.add(item.taskId));
  const root = props.tasks.find((task) => task.id === impact.value?.taskId);
  root?.predecessorIds.forEach((id) => ids.add(id));
  props.tasks
    .filter((task) => task.predecessorIds.includes(impact.value!.taskId))
    .forEach((task) => ids.add(task.id));
  return ids;
});

function left(task: ProjectTask) {
  return daysBetween(timelineStart.value, task.startDate) * dayWidth.value;
}
function scheduled(task: ProjectTask) {
  return Boolean(task.startDate && task.dueDate);
}
function width(task: ProjectTask) {
  return Math.max(
    task.type === "milestone" ? 16 : dayWidth.value,
    (daysBetween(task.startDate, task.dueDate) + 1) * dayWidth.value,
  );
}
function movedDate(date: string, shift: number) {
  return addWorkingDays(date, shift);
}
function dragOffset(task: ProjectTask) {
  return (
    daysBetween(
      task.startDate,
      movedDate(task.startDate, dragging.value?.shift || 0),
    ) * dayWidth.value
  );
}

function scrollTimeline(direction: -1 | 1) {
  const scroller = timelineScroller.value;
  if (!scroller) return;
  const taskColumn = scroller.querySelector<HTMLElement>(
    ".gantt-header--tasks",
  );
  const taskColumnWidth =
    taskColumn?.getBoundingClientRect().width || taskColumn?.offsetWidth || 0;
  const pageWidth = Math.max(
    dayWidth.value,
    scroller.clientWidth - taskColumnWidth,
  );
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  scroller.scrollBy({
    left: direction * pageWidth,
    behavior: prefersReducedMotion ? "auto" : "smooth",
  });
}

function canMove(task: ProjectTask) {
  return (
    workspace.canEditPlan &&
    task.type !== "summary" &&
    !["Complete", "Not applicable"].includes(task.status)
  );
}

function movementLabel(task: ProjectTask) {
  const dates = `${shortDate(task.startDate)} to ${shortDate(task.dueDate)}`;
  if (task.type === "summary")
    return scheduled(task)
      ? `${task.title}, ${dates}. Dates are calculated from child tasks.`
      : `${task.title}. Dates will be calculated after child tasks are added.`;
  if (["Complete", "Not applicable"].includes(task.status))
    return `${task.title}, ${dates}. Finished work is locked.`;
  return `${task.title}, ${dates}. Drag or use left and right arrow keys to propose a move.`;
}

function pointerDown(event: PointerEvent, task: ProjectTask) {
  if (!canMove(task)) return;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  dragging.value = { task, startX: event.clientX, shift: 0 };
}
function pointerMove(event: PointerEvent) {
  if (!dragging.value) return;
  dragging.value.shift = Math.round(
    (event.clientX - dragging.value.startX) / dayWidth.value,
  );
}
async function pointerUp() {
  const drag = dragging.value;
  dragging.value = null;
  if (!drag || !drag.shift) return;
  impact.value = await workspace.previewSchedule(
    drag.task.id,
    movedDate(drag.task.startDate, drag.shift),
    movedDate(drag.task.dueDate, drag.shift),
  );
}
async function keyboardMove(task: ProjectTask, days: number) {
  if (!canMove(task)) return;
  impact.value = await workspace.previewSchedule(
    task.id,
    movedDate(task.startDate, days),
    movedDate(task.dueDate, days),
  );
}
async function apply(options?: { extensionReason?: string }) {
  if (!impact.value || impact.value.canApply === false) return;
  await workspace.applySchedule(impact.value, options);
  impact.value = null;
}
</script>

<template>
  <div class="gantt">
    <div class="gantt__toolbar">
      <div>
        <div class="gantt-scale" role="group" aria-label="Timeline scale">
          <CalendarRange :size="16" aria-hidden="true" />
          <button
            type="button"
            :class="{ active: scale === 'day' }"
            :aria-pressed="scale === 'day'"
            @click="scale = 'day'"
          >
            Daily
          </button>
          <button
            type="button"
            :class="{ active: scale === 'week' }"
            :aria-pressed="scale === 'week'"
            @click="scale = 'week'"
          >
            Weekly
          </button>
        </div>
        <button
          class="icon-button"
          type="button"
          aria-label="Scroll timeline left one page"
          @click="scrollTimeline(-1)"
        >
          <ChevronLeft :size="18" aria-hidden="true" /></button
        ><button
          class="icon-button"
          type="button"
          aria-label="Scroll timeline right one page"
          @click="scrollTimeline(1)"
        >
          <ChevronRight :size="18" aria-hidden="true" />
        </button>
      </div>
      <div>
        <details class="gantt-legend">
          <summary>Bar colors and movement</summary>
          <div>
            <span><i class="legend-blue"></i>Planned or in progress</span>
            <span><i class="legend-review"></i>In review</span>
            <span><i class="legend-blocked"></i>Blocked</span>
            <span><i class="legend-complete"></i>Complete · locked</span>
            <span><i class="legend-na"></i>Not applicable · locked</span>
            <span
              ><i class="legend-summary"></i>Task collection · derived
              dates</span
            >
            <p>
              Milestones use diamonds. A link beside a task means it is waiting
              for predecessor work.
            </p>
          </div>
        </details>
        <span class="gantt-hint"
          ><GripVertical :size="14" /> Drag a bar or use arrow keys</span
        ><button
          class="button button--secondary"
          type="button"
          :aria-pressed="expanded"
          @click="expanded = !expanded"
        >
          <ZoomIn :size="16" /> {{ expanded ? "Compact" : "Expand" }}
        </button>
      </div>
    </div>
    <div ref="timelineScroller" class="gantt__scroll">
      <div
        class="gantt-grid"
        :style="{ '--timeline-width': `${timelineWidth}px` }"
      >
        <div class="gantt-header gantt-header--tasks">Task</div>
        <div
          class="gantt-header gantt-header--timeline"
          :style="{ width: `${timelineWidth}px` }"
        >
          <span
            v-for="marker in timeMarkers"
            :key="marker.date"
            :style="{
              left: `${marker.left}px`,
              width: `${marker.width}px`,
            }"
            >{{ shortDate(marker.date) }}</span
          >
        </div>
        <template v-for="task in rows" :key="task.id">
          <button
            class="gantt-task"
            :class="{
              'gantt-task--impact': impactIds.has(task.id),
              'gantt-task--internal': task.visibility === 'Internal',
            }"
            type="button"
            :style="{ paddingLeft: `${13 + task.level * 20}px` }"
            @click="emit('openTask', task.id)"
          >
            <Diamond
              v-if="task.type === 'milestone' && scheduled(task)"
              :size="12"
              fill="currentColor"
            /><span class="truncate">{{ task.title }}</span
            ><EyeOff
              v-if="workspace.isInternal && task.visibility === 'Internal'"
              :size="12"
              class="internal-marker"
              aria-label="Internal — visible only to VIPdesk project participants"
              title="Internal — visible only to VIPdesk project participants"
            />
            ><span
              v-if="task.predecessorIds.length"
              class="dependency-count"
              :title="`${task.predecessorIds.length} predecessor ${task.predecessorIds.length === 1 ? 'task' : 'tasks'}`"
              ><Link2 :size="12" />{{ task.predecessorIds.length }}</span
            ><LockKeyhole
              v-if="!canMove(task)"
              :size="12"
              aria-label="Dates locked"
            /><small>{{ task.ownerInitials }}</small>
          </button>
          <div
            class="gantt-lane"
            :class="{ 'gantt-lane--impact': impactIds.has(task.id) }"
            :style="{
              width: `${timelineWidth}px`,
              backgroundSize: `${gridSize}px 100%`,
            }"
          >
            <span class="today-line" :style="{ left: `${todayLeft}px` }"
              ><i>Today</i></span
            ><span
              v-if="boundaryLeft < timelineWidth"
              class="boundary-line"
              :style="{ left: `${boundaryLeft}px` }"
              ><i>Project target</i></span
            >
            <button
              v-if="task.type === 'milestone'"
              class="gantt-milestone"
              :class="{
                'gantt-milestone--impact': impactIds.has(task.id),
                'gantt-milestone--locked': !canMove(task),
                'gantt-milestone--internal': task.visibility === 'Internal',
              }"
              type="button"
              :style="{
                left: `${left(task) - 7 + (dragging?.task.id === task.id ? dragOffset(task) : 0)}px`,
              }"
              :aria-label="movementLabel(task)"
              :title="movementLabel(task)"
              @click="emit('openTask', task.id)"
              @pointerdown="pointerDown($event, task)"
              @pointermove="pointerMove"
              @pointerup="pointerUp"
              @keydown.left.prevent="keyboardMove(task, -1)"
              @keydown.right.prevent="keyboardMove(task, 1)"
            >
              <span></span>
            </button>
            <button
              v-else-if="scheduled(task)"
              class="gantt-bar"
              :class="[
                `gantt-bar--${task.status.toLowerCase().replace(' ', '-')}`,
                {
                  'gantt-bar--dragging': dragging?.task.id === task.id,
                  'gantt-bar--summary': task.type === 'summary',
                  'gantt-bar--locked': !canMove(task),
                  'gantt-bar--impact': impactIds.has(task.id),
                  'gantt-bar--internal': task.visibility === 'Internal',
                },
              ]"
              type="button"
              :style="{
                left: `${left(task) + (dragging?.task.id === task.id ? dragOffset(task) : 0)}px`,
                width: `${width(task)}px`,
              }"
              :aria-label="movementLabel(task)"
              :title="movementLabel(task)"
              @pointerdown="pointerDown($event, task)"
              @pointermove="pointerMove"
              @pointerup="pointerUp"
              @keydown.left.prevent="keyboardMove(task, -1)"
              @keydown.right.prevent="keyboardMove(task, 1)"
            >
              <span
                class="gantt-bar__progress"
                :style="{ width: `${task.progress}%` }"
              ></span
              ><span class="gantt-bar__label">{{
                width(task) > 90 ? task.title : ""
              }}</span
              ><span v-if="dragging?.task.id === task.id" class="drag-date"
                >{{ shortDate(movedDate(task.startDate, dragging.shift)) }} –
                {{ shortDate(movedDate(task.dueDate, dragging.shift)) }}</span
              >
            </button>
            <span v-else class="gantt-unscheduled"
              >Add child tasks to schedule</span
            >
          </div>
        </template>
      </div>
    </div>
    <ScheduleImpactDialog
      :impact="impact"
      :can-extend="workspace.canManageProject"
      @close="impact = null"
      @apply="apply"
    />
  </div>
</template>

<style scoped>
.gantt {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  background: white;
}
.gantt__toolbar {
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 12px;
  border-bottom: 1px solid var(--rule);
}
.gantt__toolbar > div {
  display: flex;
  align-items: center;
  gap: 5px;
}
.gantt-scale {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 3px 2px 8px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 650;
}
.gantt-scale > svg {
  margin-right: 3px;
}
.gantt-scale button {
  min-height: 26px;
  padding: 0 8px;
  border-radius: 3px;
  background: transparent;
  color: var(--text-soft);
  cursor: pointer;
}
.gantt-scale button:hover {
  background: var(--sheet-strong);
}
.gantt-scale button.active {
  background: var(--ink-900);
  color: white;
}
.gantt-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--text-soft);
  font-size: 11px;
}
.gantt-legend {
  position: relative;
}
.gantt-legend summary {
  min-height: 32px;
  display: flex;
  align-items: center;
  padding: 0 9px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 650;
  cursor: pointer;
  list-style: none;
}
.gantt-legend > div {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  width: 265px;
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: white;
  box-shadow: var(--shadow-menu);
}
.gantt-legend span {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-soft);
  font-size: 11px;
}
.gantt-legend i {
  width: 26px;
  height: 8px;
  border-radius: 2px;
  background: #6b8fab;
}
.gantt-legend .legend-review {
  background: #7b6b96;
}
.gantt-legend .legend-blocked {
  background: #a65151;
}
.gantt-legend .legend-complete {
  background: #548a70;
}
.gantt-legend .legend-na {
  border: 1px solid #7b8583;
  background: repeating-linear-gradient(
    135deg,
    #f0f2f1,
    #f0f2f1 3px,
    #aab2b0 3px,
    #aab2b0 5px
  );
}
.gantt-legend .legend-summary {
  background: var(--ink-800);
}
.gantt-legend p {
  margin: 3px 0 0;
  padding-top: 9px;
  border-top: 1px solid var(--rule);
  color: var(--text-soft);
  font-size: 11px;
}
.gantt__scroll {
  min-height: 0;
  overflow: auto;
}
.gantt-grid {
  width: calc(340px + var(--timeline-width));
  display: grid;
  grid-template-columns: 340px var(--timeline-width);
  grid-auto-rows: 42px;
}
.gantt-header {
  position: sticky;
  top: 0;
  z-index: 9;
  border-bottom: 1px solid var(--rule-strong);
  background: #eef2f1;
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.gantt-header--tasks {
  left: 0;
  z-index: 11;
  display: flex;
  align-items: center;
  padding: 0 13px;
  border-right: 1px solid var(--rule-strong);
}
.gantt-header--timeline {
  position: sticky;
  left: 340px;
  overflow: hidden;
}
.gantt-header--timeline span {
  position: absolute;
  inset-block: 0;
  display: flex;
  align-items: center;
  padding-left: 7px;
  border-right: 1px solid var(--rule);
}
.gantt-task {
  position: sticky;
  left: 0;
  z-index: 6;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  padding-right: 9px;
  border-right: 1px solid var(--rule-strong);
  border-bottom: 1px solid var(--rule);
  background: white;
  text-align: left;
  cursor: pointer;
}
.gantt-task:hover {
  background: var(--sheet-subtle);
}
.gantt-task--impact {
  background: #fff8e8;
  box-shadow: inset 3px 0 var(--warning);
}
.gantt-task--internal {
  background: #fbf9fc;
}
.gantt-task .internal-marker {
  color: var(--internal);
}
.gantt-task svg {
  flex: 0 0 auto;
  color: var(--shared);
}
.gantt-task small {
  margin-left: auto;
  color: var(--text-faint);
}
.dependency-count {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--shared);
  font-size: 10px;
}
.gantt-lane {
  position: relative;
  border-bottom: 1px solid var(--rule);
  background-image: linear-gradient(
    to right,
    transparent calc(100% - 1px),
    #e6ebe9 calc(100% - 1px)
  );
}
.gantt-lane--impact {
  background-color: #fffaf0;
}
.gantt-unscheduled {
  position: absolute;
  top: 13px;
  left: 12px;
  color: var(--text-faint);
  font-size: 10px;
}
.today-line,
.boundary-line {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #2879bb;
  pointer-events: none;
}
.boundary-line {
  background: var(--danger);
}
.today-line i,
.boundary-line i {
  display: none;
}
.gantt-grid > .gantt-lane:nth-of-type(2) .today-line i,
.gantt-grid > .gantt-lane:nth-of-type(2) .boundary-line i {
  display: block;
  position: absolute;
  top: 2px;
  left: 4px;
  color: inherit;
  font-size: 9px;
  font-style: normal;
  white-space: nowrap;
}
.gantt-bar {
  position: absolute;
  z-index: 3;
  top: 11px;
  height: 20px;
  overflow: visible;
  border-radius: 3px;
  background: #6b8fab;
  color: white;
  cursor: grab;
  transition: box-shadow 120ms var(--ease);
  touch-action: none;
}
.gantt-bar:active {
  cursor: grabbing;
}
.gantt-bar:focus {
  box-shadow: 0 0 0 3px rgba(31, 112, 193, 0.3);
}
.gantt-bar--complete {
  background: #548a70;
}
.gantt-bar--not-applicable {
  border: 1px solid #7b8583;
  background: repeating-linear-gradient(
    135deg,
    #f0f2f1,
    #f0f2f1 5px,
    #9fa8a6 5px,
    #9fa8a6 7px
  );
  color: var(--text);
}
.gantt-bar--blocked {
  background: #a65151;
}
.gantt-bar--in-review {
  background: #7b6b96;
}
.gantt-bar--summary {
  top: 9px;
  height: 24px;
  border-radius: 2px;
  background: var(--ink-800);
}
.gantt-bar--internal {
  outline: 1px dashed var(--internal);
  outline-offset: 2px;
}
.gantt-bar--locked,
.gantt-milestone--locked {
  cursor: not-allowed;
}
.gantt-milestone--internal {
  box-shadow:
    0 0 0 1px var(--internal),
    0 0 0 3px var(--internal-soft);
}
.gantt-bar--dragging {
  z-index: 7;
  opacity: 0.88;
  box-shadow: 0 7px 16px rgba(8, 27, 46, 0.22);
}
.gantt-bar--impact {
  box-shadow: 0 0 0 3px rgba(162, 95, 8, 0.38);
}
.gantt-bar__progress {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.25);
}
.gantt-bar__label {
  position: relative;
  z-index: 1;
  display: block;
  padding: 2px 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
}
.drag-date {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 7px);
  transform: translateX(-50%);
  padding: 4px 7px;
  border-radius: 4px;
  background: var(--ink-950);
  color: white;
  font-size: 10px;
  white-space: nowrap;
}
.gantt-milestone {
  position: absolute;
  z-index: 4;
  top: 13px;
  width: 16px;
  height: 16px;
  padding: 0;
  transform: rotate(45deg);
  border: 2px solid white;
  background: var(--shared);
  box-shadow: 0 0 0 1px var(--shared);
  cursor: pointer;
}
.gantt-milestone:focus {
  box-shadow: 0 0 0 3px rgba(31, 112, 193, 0.32);
}
.gantt-milestone--impact {
  box-shadow: 0 0 0 4px rgba(162, 95, 8, 0.38);
}
.gantt-milestone--locked {
  background: var(--success);
  box-shadow: 0 0 0 1px var(--success);
}
@media (pointer: coarse) {
  .gantt-bar::after,
  .gantt-milestone::after {
    content: "";
    position: absolute;
    inset: -12px -6px;
  }
}
@media (max-width: 700px) {
  .gantt-hint {
    display: none;
  }
  .gantt-grid {
    grid-template-columns: 190px var(--timeline-width);
    width: calc(190px + var(--timeline-width));
  }
  .gantt-header--timeline {
    left: 190px;
  }
  .gantt-header--tasks {
    width: 190px;
  }
  .gantt-task {
    width: 190px;
  }
  .gantt__toolbar .button {
    padding-inline: 8px;
  }
}
</style>
